import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useState, useMemo } from 'react';
import { useStore } from '../../hooks/useStore';
import { fetchProducts, bulkOptimize, fetchProductFacets } from '../../lib/api';
import type { Product } from '../../types';
import { safeArray } from '../../lib/safe';

export const Route = createFileRoute('/products/')({
  component: ProductsPage,
});

const PAGE_SIZES = [25, 50, 100] as const;

function ProductCard({ product }: { product: Product }) {
  const isOptimized = product.tags?.includes('ai-optimized');
  const jobStatus = product.status;
  let status: string;
  if (isOptimized || jobStatus === 'completed') status = 'optimized';
  else if (jobStatus === 'failed') status = 'failed';
  else if (jobStatus === 'processing') status = 'processing';
  else if (jobStatus === 'pending') status = 'pending';
  else status = 'unoptimized';

  const statusStyles: Record<string, { bg: string; label: string }> = {
    optimized: { bg: 'bg-emerald-100 text-emerald-700', label: 'Optimized' },
    failed: { bg: 'bg-red-100 text-red-700', label: 'Failed' },
    processing: { bg: 'bg-blue-100 text-blue-700', label: 'Processing' },
    pending: { bg: 'bg-amber-100 text-amber-700', label: 'Pending' },
    unoptimized: { bg: 'bg-slate-100 text-slate-600', label: 'Unoptimized' },
  };
  const s = statusStyles[status] || statusStyles.unoptimized;

  return (
    <Link
      to="/products/$productId"
      params={{ productId: String(product.id) }}
      className="bg-white rounded-xl border border-[var(--ccb-border)] overflow-hidden hover:shadow-md transition-shadow group"
    >
      <div className="aspect-square bg-slate-50 overflow-hidden">
        {product.image?.src ? (
          <img
            src={product.image.src}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" />
            </svg>
          </div>
        )}
      </div>
      <div className="p-3 space-y-1.5">
        <h3 className="text-sm font-medium text-[var(--ccb-text)] line-clamp-2 leading-tight">{product.title}</h3>
        <p className="text-xs text-[var(--ccb-text-secondary)]">{product.vendor}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-[var(--ccb-text-secondary)]">{product.product_type || '\u2014'}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${s.bg}`}>
            {s.label}
          </span>
        </div>
      </div>
    </Link>
  );
}

function ProductsPage() {
  const { activeStore } = useStore();
  const queryClient = useQueryClient();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'optimized' | 'unoptimized' | 'failed'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [vendorFilter, setVendorFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const statusParam = filterMode === 'all' ? '' : filterMode === 'optimized' ? 'completed' : filterMode;

  // Derive server-side sort params from TanStack Table sorting state
  const sortBy = sorting.length > 0 ? sorting[0].id : '';
  const sortOrder = sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : '';

  // Facets query for filter dropdowns
  const { data: facets } = useQuery({
    queryKey: ['product-facets', activeStore.shop],
    queryFn: () => fetchProductFacets(activeStore.shop),
    staleTime: 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['products', activeStore.shop, page, pageSize, statusParam, globalFilter, sortBy, sortOrder, vendorFilter, typeFilter, categoryFilter],
    queryFn: () => fetchProducts(activeStore.shop, {
      page: String(page),
      pageSize: String(pageSize),
      status: statusParam,
      search: globalFilter,
      sortBy,
      sortOrder,
      vendor: vendorFilter,
      product_type: typeFilter,
      category: categoryFilter,
    }),
    placeholderData: (prev) => prev, // keep previous data while loading
  });

  const bulkMutation = useMutation({
    mutationFn: (opts: { force?: boolean }) => bulkOptimize(activeStore.shop, opts),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['products'] }),
  });

  const products = safeArray<Product>(data?.products);
  const totalPages = data?.totalPages || 1;
  const total = data?.total || 0;

  const hasActiveFilters = vendorFilter !== '' || typeFilter !== '' || categoryFilter !== '';

  const columns = useMemo<ColumnDef<Product>[]>(
    () => [
      {
        accessorKey: 'image',
        header: '',
        size: 48,
        enableSorting: false,
        cell: ({ row }) => {
          const src = row.original.image?.src;
          return src ? (
            <img src={src} alt="" className="w-10 h-10 rounded object-cover" loading="lazy" />
          ) : (
            <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center text-xs text-slate-400">{'\u2014'}</div>
          );
        },
      },
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => (
          <Link
            to="/products/$productId"
            params={{ productId: String(row.original.id) }}
            className="font-medium text-[var(--ccb-text)] hover:text-[var(--ccb-primary)] transition-colors"
          >
            {row.original.title}
          </Link>
        ),
      },
      { accessorKey: 'vendor', header: 'Vendor', size: 140 },
      { accessorKey: 'product_type', header: 'Type', size: 130 },
      {
        accessorKey: 'shop',
        header: 'Store',
        size: 100,
        cell: ({ getValue }) => {
          const shop = getValue<string>();
          if (!shop) return '\u2014';
          const short = shop.includes('casual-chic-boutique') ? 'CCB'
            : shop.includes('casual-chic-luxe') ? 'CCL'
            : shop.includes('casual-chic-collection') ? 'CCC'
            : shop.includes('q0gtxj') ? 'CCS'
            : shop.includes('bargains') ? 'TBD'
            : shop.split('.')[0];
          return <span className="text-xs text-[var(--ccb-text-secondary)]">{short}</span>;
        },
      },
      {
        id: 'optimizationStatus',
        header: 'Status',
        size: 120,
        accessorFn: (row) => {
          const isOptimized = row.tags?.includes('ai-optimized');
          const jobStatus = row.status;
          if (isOptimized || jobStatus === 'completed') return 'optimized';
          if (jobStatus === 'failed') return 'failed';
          if (jobStatus === 'processing') return 'processing';
          if (jobStatus === 'pending') return 'pending';
          return 'unoptimized';
        },
        cell: ({ getValue }) => {
          const val = getValue<string>();
          const styles: Record<string, { bg: string; label: string }> = {
            optimized: { bg: 'bg-emerald-50 text-emerald-700', label: 'Optimized' },
            failed: { bg: 'bg-red-50 text-red-700', label: 'Failed' },
            processing: { bg: 'bg-blue-50 text-blue-700', label: 'Processing' },
            pending: { bg: 'bg-amber-50 text-amber-700', label: 'Pending' },
            unoptimized: { bg: 'bg-slate-100 text-slate-600', label: 'Unoptimized' },
          };
          const s = styles[val] || styles.unoptimized;
          return <span className={`text-xs px-2 py-0.5 rounded-full ${s.bg}`}>{s.label}</span>;
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data: products,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      setSorting(updater);
      setPage(1); // reset to first page when sorting changes
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
  });

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-xl font-semibold text-[var(--ccb-text)]">
          Products
          {total > 0 && <span className="text-sm font-normal text-[var(--ccb-text-secondary)] ml-2">({total.toLocaleString()} total)</span>}
        </h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search products..."
            value={globalFilter}
            onChange={(e) => { setGlobalFilter(e.target.value); setPage(1); }}
            className="border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
          />

          {/* Filters toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
              showFilters || hasActiveFilters
                ? 'border-[var(--ccb-primary)] text-[var(--ccb-primary)] bg-[var(--ccb-primary)]/5'
                : 'border-[var(--ccb-border)] text-[var(--ccb-text-secondary)] hover:text-[var(--ccb-text)] hover:border-[var(--ccb-text-secondary)]'
            }`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Filters
            {hasActiveFilters && (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[var(--ccb-primary)] text-white text-[10px] font-bold">
                {(vendorFilter ? 1 : 0) + (typeFilter ? 1 : 0) + (categoryFilter ? 1 : 0)}
              </span>
            )}
          </button>

          {/* Filter pills */}
          <div className="flex bg-slate-100 rounded-lg p-0.5 text-xs font-medium">
            {(['all', 'optimized', 'unoptimized', 'failed'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => { setFilterMode(mode); setPage(1); }}
                className={`px-3 py-1 rounded-md capitalize transition-colors ${
                  filterMode === mode ? 'bg-white text-[var(--ccb-text)] shadow-sm' : 'text-[var(--ccb-text-secondary)]'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>

          <button
            onClick={() => bulkMutation.mutate({})}
            disabled={bulkMutation.isPending}
            className="bg-[var(--ccb-primary)] text-white text-sm font-medium px-4 py-1.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {bulkMutation.isPending ? 'Running...' : 'Bulk Optimize'}
          </button>

          {/* View toggle */}
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-[var(--ccb-primary)]' : 'text-slate-400 hover:text-slate-600'}`}
              title="Table view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-[var(--ccb-primary)]' : 'text-slate-400 hover:text-slate-600'}`}
              title="Card view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Collapsible filter panel */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-[var(--ccb-border)] p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--ccb-text-secondary)] uppercase tracking-wide">Vendor</label>
              <select
                value={vendorFilter}
                onChange={(e) => { setVendorFilter(e.target.value); setPage(1); }}
                className="border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-sm min-w-[180px] focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30 bg-white text-[var(--ccb-text)]"
              >
                <option value="">All Vendors</option>
                {facets?.vendors?.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.value} ({v.count})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--ccb-text-secondary)] uppercase tracking-wide">Product Type</label>
              <select
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                className="border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-sm min-w-[180px] focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30 bg-white text-[var(--ccb-text)]"
              >
                <option value="">All Types</option>
                {facets?.product_types?.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.value} ({t.count})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--ccb-text-secondary)] uppercase tracking-wide">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                className="border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-sm min-w-[180px] focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30 bg-white text-[var(--ccb-text)]"
              >
                <option value="">All Categories</option>
                {facets?.categories?.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.value} ({cat.count})
                  </option>
                ))}
              </select>
            </div>

            {hasActiveFilters && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-transparent uppercase tracking-wide">&nbsp;</label>
                <button
                  onClick={() => { setVendorFilter(''); setTypeFilter(''); setCategoryFilter(''); setPage(1); }}
                  className="text-sm text-[var(--ccb-text-secondary)] hover:text-[var(--ccb-text)] px-3 py-1.5 rounded-lg border border-[var(--ccb-border)] hover:border-[var(--ccb-text-secondary)] transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Products display */}
      <div className="bg-white rounded-xl border border-[var(--ccb-border)] overflow-hidden">
        {isLoading && !data ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">Loading products...</div>
        ) : (
          <>
            {viewMode === 'cards' ? (
              <div className="p-4">
                {products.length === 0 ? (
                  <div className="py-12 text-center text-[var(--ccb-text-secondary)]">No products found</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {products.map((p) => <ProductCard key={String(p.id)} product={p} />)}
                  </div>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    {table.getHeaderGroups().map((hg) => (
                      <tr key={hg.id} className="border-b border-[var(--ccb-border)]">
                        {hg.headers.map((header) => (
                          <th
                            key={header.id}
                            onClick={header.column.getToggleSortingHandler()}
                            className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide cursor-pointer select-none hover:text-[var(--ccb-text)]"
                            style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                          >
                            <div className="flex items-center gap-1">
                              {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                              {{ asc: ' \u2191', desc: ' \u2193' }[header.column.getIsSorted() as string] ?? ''}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="border-b border-[var(--ccb-border)] last:border-0 hover:bg-slate-50/50 transition-colors">
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-4 py-3">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {table.getRowModel().rows.length === 0 && (
                      <tr>
                        <td colSpan={columns.length} className="px-4 py-12 text-center text-[var(--ccb-text-secondary)]">
                          No products found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--ccb-border)] text-sm text-[var(--ccb-text-secondary)]">
              <div className="flex items-center gap-3">
                <span>
                  Page {page} of {totalPages} · {total.toLocaleString()} products
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="border border-[var(--ccb-border)] rounded px-2 py-1 text-xs"
                >
                  {PAGE_SIZES.map((s) => (
                    <option key={s} value={s}>{s} per page</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(1)}
                  disabled={page <= 1}
                  className="px-2 py-1 rounded border border-[var(--ccb-border)] disabled:opacity-30 hover:bg-slate-50 text-xs"
                >
                  First
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 rounded border border-[var(--ccb-border)] disabled:opacity-30 hover:bg-slate-50"
                >
                  Previous
                </button>
                <span className="text-xs font-medium">{page}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 rounded border border-[var(--ccb-border)] disabled:opacity-30 hover:bg-slate-50"
                >
                  Next
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  className="px-2 py-1 rounded border border-[var(--ccb-border)] disabled:opacity-30 hover:bg-slate-50 text-xs"
                >
                  Last
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
